import React from "react";
import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import BugsnagPerformance from "@bugsnag/browser-performance";

const releaseStage = import.meta.env.VITE_ENV;
// This app has its own Bugsnag project, so the key comes from the build env
// rather than being hard-coded the way app-frontend does it. Without a key
// Bugsnag.start() throws, so treat a missing key as "reporting off".
const apiKey = import.meta.env.VITE_BUGSNAG_API_KEY;
const isReportingEnabled =
  ["production", "staging"].includes(releaseStage) && Boolean(apiKey);

// Errors we deliberately drop so real bugs aren't buried in noise.
// These are environmental (network, stale cache, browser extensions,
// cross-origin scripts) — not defects in our code.
const NOISE_MESSAGE_PATTERNS = [
  /ResizeObserver loop/i, // benign layout warning
  /^Script error\.?$/i, // cross-origin third-party script, no detail
  /Request aborted/i, // navigation/cancel mid-request
  /\bcanceled\b/i, // aborted fetch/axios
];

const EXTENSION_FRAME = /^(chrome|moz|safari(-web)?)-extension:\/\//i;

function isNoise(event) {
  const err = event.errors && event.errors[0];
  if (!err) return false;

  const message = err.errorMessage || "";
  if (NOISE_MESSAGE_PATTERNS.some((re) => re.test(message))) return true;

  // Errors whose every stack frame lives in a browser extension are not ours.
  const frames = err.stacktrace || [];
  if (
    frames.length &&
    frames.every((f) => EXTENSION_FRAME.test(f.file || ""))
  ) {
    return true;
  }

  return false;
}

let ErrorBoundary = ({ children }) => children;

if (isReportingEnabled) {
  Bugsnag.start({
    apiKey,
    plugins: [new BugsnagPluginReact()],
    generateAnonymousId: false,
    samplingProbability: 1.0,
    enabledReleaseStages: ["staging", "production"],
    releaseStage,
    appVersion: import.meta.env.VITE_APP_VERSION,
    autoTrackSessions: false,
    // Return false to discard the event so it never reaches the dashboard.
    onError: (event) => {
      if (isNoise(event)) return false;
    },
  });

  BugsnagPerformance.start({
    apiKey,
    enabledReleaseStages: ["staging", "production"],
    releaseStage,
    appVersion: import.meta.env.VITE_APP_VERSION,
  });

  ErrorBoundary = Bugsnag.getPlugin("react").createErrorBoundary(React);
}

export { ErrorBoundary };
